import { useState, useCallback, useEffect, useRef } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/services/api"
import {
  Building2,
  Users,
  Settings,
  LayoutDashboard,
  Search,
  Map,
  UserCog,
  UsersRound,
  Tag,
  Mail,
  Inbox,
  Sparkles,
  MessageSquare,
  Globe,
  Shield,
  Building,
  ClipboardList,
  AlertTriangle,
  UserCheck,
  FileText,
  FileCheck,
  CheckSquare,
  BarChart3,
  Receipt,
  CreditCard,
  ScanLine,
  FileSpreadsheet,
  ChevronDown,
  ChevronsUpDown,
  ChevronsDownUp,
  Command,
  Star,
  Kanban,
  SquareCheckBig,
  FlaskConical,
  BookOpen,
  FolderOpen,
  Clock,
  Bot,
  Palmtree,
  Calendar,
  Banknote,
  Thermometer,
  FolderClock,
  FolderTree,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/store/authStore"
import { useModuleStore } from "@/store/moduleStore"
import { ScrollArea } from "@/components/ui/scroll-area"
// Separator nicht mehr genutzt (collapsed-Modus nutzt jetzt Icons)
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"

export interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  /** Wenn gesetzt, wird das Item nur bei einer dieser Rollen angezeigt */
  requiredRoles?: string[]
  /** Wenn gesetzt, wird ohne Admin-Bypass auf diese Rollen geprueft */
  strictRoles?: string[]
  /** Wenn gesetzt, wird das Item nur mit dieser Permission angezeigt */
  requiredPermission?: string
  /** Wenn gesetzt, wird das Item nur angezeigt wenn dieses Modul aktiviert ist */
  requiredModule?: string
  /** Optionale Sektion fuer die CommandPalette */
  section?: string
}

const arbeitsplatzNavItems: NavItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "Organisationen",
    href: "/organizations",
    icon: Building2,
  },
  {
    title: "Kontakte",
    href: "/contacts",
    icon: Users,
  },
  // Ninox-Einträge entfernt: abgelöst durch die eigenständige nxtools-Anwendung
  // (erreichbar über den App-Switcher im Header, Etappe 6).
  {
    title: "Tagebuch",
    href: "/tagebuch",
    icon: BookOpen,
  },
  {
    title: "Mail-Briefing",
    href: "/email-summaries",
    icon: Inbox,
  },
  {
    title: "Karte",
    href: "/map",
    icon: Map,
  },
  {
    title: "Suche",
    href: "/search",
    icon: Search,
  },
  {
    title: "KI-Import",
    href: "/ai-import",
    icon: Sparkles,
  },
]

const projektNavItems: NavItem[] = [
  {
    title: "Projektzeit",
    href: "/hr/project-time",
    icon: FolderClock,
    requiredModule: "hr",
    requiredPermission: "hr_project:read",
  },
  {
    title: "Projekte",
    href: "/hr/projects",
    icon: FolderTree,
    requiredModule: "hr",
    requiredPermission: "hr_project:read",
  },
  {
    title: "Projektbase",
    href: "/projektbase",
    icon: FolderOpen,
    requiredModule: "projektbase",
  },
  {
    title: "Projektbase Anleitung",
    href: "/projektbase/anleitung",
    icon: BookOpen,
    requiredModule: "projektbase",
  },
]

const dsgvoNavItems: NavItem[] = [
  {
    title: "DSGVO Dashboard",
    href: "/dsgvo",
    icon: Shield,
    requiredModule: "dsgvo",
  },
  {
    title: "Dienstleister",
    href: "/dsgvo/vendors",
    icon: Building,
    requiredModule: "dsgvo",
  },
  {
    title: "AV-Verträge",
    href: "/dsgvo/avv",
    icon: FileCheck,
    requiredModule: "dsgvo",
  },
  {
    title: "VVT",
    href: "/dsgvo/vvt",
    icon: ClipboardList,
    requiredModule: "dsgvo",
  },
  {
    title: "Datenpannen",
    href: "/dsgvo/incidents",
    icon: AlertTriangle,
    requiredModule: "dsgvo",
  },
  {
    title: "Betroffenenrechte",
    href: "/dsgvo/dsr",
    icon: UserCheck,
    requiredModule: "dsgvo",
  },
  {
    title: "Dokumente",
    href: "/dsgvo/documents",
    icon: FileText,
    requiredModule: "dsgvo",
  },
  {
    title: "Aufgaben",
    href: "/dsgvo/tasks",
    icon: CheckSquare,
    requiredModule: "dsgvo",
  },
]

const rechnungenNavItems: NavItem[] = [
  {
    title: "Rechnungen Dashboard",
    href: "/rechnungen",
    icon: BarChart3,
    requiredModule: "rechnungen",
    requiredPermission: "rechnungen:read",
  },
  {
    title: "Ausgangsrechnungen",
    href: "/rechnungen/outgoing",
    icon: Receipt,
    requiredModule: "rechnungen",
    requiredPermission: "rechnungen:read",
  },
  {
    title: "Eingangsrechnungen",
    href: "/rechnungen/invoices",
    icon: Receipt,
    requiredModule: "rechnungen",
    requiredPermission: "rechnungen:read",
  },
  {
    title: "Zahllaeufe",
    href: "/rechnungen/payment-runs",
    icon: CreditCard,
    requiredModule: "rechnungen",
    requiredPermission: "rechnungen:payment_runs",
  },
  {
    title: "OCR Import",
    href: "/rechnungen/ocr",
    icon: ScanLine,
    requiredModule: "rechnungen",
    requiredPermission: "rechnungen:write",
  },
  {
    title: "Liquiditaetsplanung",
    href: "/rechnungen/liquidity",
    icon: FileSpreadsheet,
    requiredModule: "rechnungen",
    requiredPermission: "rechnungen:read",
  },
  {
    title: "Banking",
    href: "/rechnungen/banking",
    icon: Banknote,
    requiredModule: "rechnungen",
    strictRoles: ["chef"],
  },
]

const ticketNavItems: NavItem[] = [
  {
    title: "Ticket-Board",
    href: "/tickets",
    icon: Kanban,
    requiredModule: "tickets",
  },
  {
    title: "Aufgaben",
    href: "/aufgaben",
    icon: SquareCheckBig,
  },
  {
    title: "Tester-Meldungen",
    href: "/tester-meldungen",
    icon: FlaskConical,
  },
]

const hrNavItems: NavItem[] = [
  {
    title: "Team-Übersicht",
    href: "/hr/team-overview",
    icon: Calendar,
    requiredRoles: ["hr_teamlead", "hr_manager"],
    requiredModule: "hr",
  },
  {
    title: "HR Dashboard",
    href: "/hr",
    icon: LayoutDashboard,
    requiredRoles: ["hr_manager"],
    requiredModule: "hr",
  },
  {
    title: "Mitarbeiter",
    href: "/hr/employees",
    icon: Users,
    requiredRoles: ["hr_manager"],
    requiredModule: "hr",
  },
  {
    title: "Zeiterfassung",
    href: "/hr/time-tracking",
    icon: Clock,
    requiredRoles: ["hr_teamlead", "hr_manager"],
    requiredModule: "hr",
  },
  {
    title: "Urlaub",
    href: "/hr/vacation",
    icon: Palmtree,
    requiredRoles: ["hr_teamlead", "hr_manager"],
    requiredModule: "hr",
  },
  {
    title: "Krankmeldungen",
    href: "/hr/sick-leave",
    icon: Thermometer,
    requiredRoles: ["hr_teamlead", "hr_manager"],
    requiredModule: "hr",
  },
  {
    title: "Auszahlungen",
    href: "/hr/payouts",
    icon: Banknote,
    requiredRoles: ["hr_teamlead", "hr_manager"],
    requiredModule: "hr",
  },
  {
    title: "Kalender",
    href: "/hr/calendar",
    icon: Calendar,
    requiredRoles: ["hr_manager"],
    requiredModule: "hr",
  },
  {
    title: "Arbeitszeitmodelle",
    href: "/hr/work-time-models",
    icon: Clock,
    requiredRoles: ["hr_manager"],
    requiredModule: "hr",
  },
  {
    title: "Journal",
    href: "/hr/journal",
    icon: BookOpen,
    requiredRoles: ["hr_manager"],
    requiredModule: "hr",
  },
]

const adminNavItems: NavItem[] = [
  {
    title: "Testerwerk",
    href: "/admin/testerwerk",
    icon: FlaskConical,
    requiredRoles: ["admin"],
  },
  {
    title: "Benutzer",
    href: "/admin/users",
    icon: UserCog,
    requiredRoles: ["admin"],
  },
  {
    title: "Benutzergruppen",
    href: "/admin/user-groups",
    icon: UsersRound,
    requiredRoles: ["admin"],
  },
  {
    title: "Ticketgruppen",
    href: "/admin/ticket-groups",
    icon: Tag,
    requiredRoles: ["admin"],
  },
  {
    title: "Matrix",
    href: "/admin/matrix",
    icon: MessageSquare,
    requiredRoles: ["admin"],
    requiredModule: "matrix",
  },
  {
    title: "Mattermost",
    href: "/admin/mattermost",
    icon: MessageSquare,
    requiredRoles: ["admin"],
    requiredModule: "mattermost",
  },
  {
    title: "E-Mail / SMTP",
    href: "/admin/email",
    icon: Mail,
    requiredRoles: ["admin"],
  },
  {
    title: "Seiten",
    href: "/admin/pages",
    icon: Globe,
    requiredRoles: ["admin"],
  },
  {
    title: "Einstellungen",
    href: "/admin/settings",
    icon: Settings,
    requiredRoles: ["admin"],
  },
  {
    title: "OpenCami",
    href: "/opencami",
    icon: Bot,
    requiredRoles: ["admin"],
  },
]

// Alle Nav-Items als flaches Array fuer Favoriten-Lookup + CommandPalette
export const allNavItems: NavItem[] = [
  { title: "Mein Arbeitsplatz", href: "/hr/my", icon: Users, section: "Arbeitsplatz" },
  ...arbeitsplatzNavItems.map((i) => ({ ...i, section: "Werkbank" })),
  ...projektNavItems.map((i) => ({ ...i, section: "Projekte" })),
  ...ticketNavItems.map((i) => ({ ...i, section: "Tickets" })),
  ...dsgvoNavItems.map((i) => ({ ...i, section: "DSGVO" })),
  ...rechnungenNavItems.map((i) => ({ ...i, section: "Rechnungen" })),
  ...hrNavItems.map((i) => ({ ...i, section: "Personal" })),
  ...adminNavItems.map((i) => ({ ...i, section: "Administration" })),
]

// =========================================================================
// LocalStorage Helpers
// =========================================================================

const STORAGE_KEY_SECTIONS = "netzcrm-sidebar-sections"
const STORAGE_KEY_FAVORITES = "netzcrm-sidebar-favorites"
const NON_FAVORITE_HREFS = new Set(["/hr/my"])

function loadSectionState(): Record<string, boolean> {
  // Default: leeres Objekt -> SectionHeader oeffnet nur die Sektion mit aktiver Route.
  // User-Aenderungen werden persistiert und ueberschreiben das Default.
  try {
    const data = localStorage.getItem(STORAGE_KEY_SECTIONS)
    return data ? JSON.parse(data) : {}
  } catch {
    return {}
  }
}

function saveSectionState(state: Record<string, boolean>) {
  localStorage.setItem(STORAGE_KEY_SECTIONS, JSON.stringify(state))
}

function loadFavorites(): string[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY_FAVORITES)
    return data ? JSON.parse(data).filter((href: string) => !NON_FAVORITE_HREFS.has(href)) : []
  } catch {
    return []
  }
}

function saveFavorites(favorites: string[]) {
  localStorage.setItem(STORAGE_KEY_FAVORITES, JSON.stringify(favorites))
}

// =========================================================================
// Sidebar Component
// =========================================================================

interface SidebarProps {
  collapsed?: boolean
  onOpenPalette?: () => void
}

export function Sidebar({ collapsed = false, onOpenPalette }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { hasRole, hasPermission, isAdmin } = useAuthStore()
  const { isModuleEnabled } = useModuleStore()
  const [sectionState, setSectionState] = useState(loadSectionState)
  const [favorites, setFavorites] = useState(loadFavorites)
  const prefsAppliedRef = useRef(false)
  const [ticketUnread, setTicketUnread] = useState(0)
  const [isMac, setIsMac] = useState(false)
  useEffect(() => {
    setIsMac(typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform))
  }, [])

  // Generischer Filter: Modul + Rolle
  const canSeeItem = (item: NavItem) => {
    if (item.requiredModule && !isModuleEnabled(item.requiredModule)) return false
    if (item.requiredPermission && !hasPermission(item.requiredPermission)) return false
    if (item.strictRoles) {
      return item.strictRoles.some((role) => hasRole(role))
    }
    if (item.requiredRoles && !isAdmin()) {
      return item.requiredRoles.some((role) => hasRole(role))
    }
    return true
  }
  const filterItems = (items: NavItem[]) => items.filter(canSeeItem)

  const filteredHrNavItems = filterItems(hrNavItems)
  const filteredArbeitsplatzItems = filterItems(arbeitsplatzNavItems)
  const filteredProjektItems = filterItems(projektNavItems)
  const filteredTicketItems = filterItems(ticketNavItems)
  const filteredDsgvoItems = filterItems(dsgvoNavItems)
  const filteredRechnungenItems = filterItems(rechnungenNavItems)
  const filteredAdminItems = filterItems(adminNavItems)

  // Ticket unread count polling
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await fetch("/api/v1/tickets/unread-count", {
          headers: { "Authorization": `Bearer ${localStorage.getItem("access_token") || ""}` },
        })
        if (res.ok) {
          const data = await res.json()
          setTicketUnread(data.count ?? 0)
        }
      } catch { /* ignore */ }
    }
    fetchUnread()
    const interval = setInterval(fetchUnread, 60000)
    return () => clearInterval(interval)
  }, [])

  const isActive = (href: string) => {
    if (href === "/") {
      return location.pathname === "/"
    }
    return location.pathname.startsWith(href)
  }

  const toggleSection = useCallback((section: string) => {
    setSectionState((prev) => {
      const shouldOpen = !(prev[section] ?? false)
      const next: Record<string, boolean> = {}
      for (const key of SECTION_KEYS) next[key] = key === section ? shouldOpen : false
      saveSectionState(next)
      return next
    })
  }, [])

  const toggleFavorite = useCallback((href: string) => {
    if (NON_FAVORITE_HREFS.has(href)) return
    setFavorites((prev) => {
      const next = prev.includes(href)
        ? prev.filter((f) => f !== href)
        : [...prev, href]
      saveFavorites(next)
      api.auth.updatePreferences({ sidebarFavorites: next }).catch(() => {
        // localStorage bleibt als Fallback erhalten; beim naechsten Laden wird erneut synchronisiert.
      })
      return next
    })
  }, [])

  const { data: serverPrefs } = useQuery({
    queryKey: ["user-preferences"],
    queryFn: () => api.auth.getPreferences(),
    staleTime: 5 * 60 * 1000,
  })

  const { data: branding } = useQuery({
    queryKey: ["public-branding"],
    queryFn: () => api.branding.get(),
    staleTime: 5 * 60 * 1000,
  })

  const sidebarLogoUrl = collapsed
    ? branding?.logos?.logo_small?.url || branding?.logos?.logo_main?.url || "/nf512512.png"
    : branding?.logos?.logo_main?.url || branding?.logos?.logo_small?.url || "/netzfabrik-logo.png"
  const sidebarLogoAlt = branding?.organizationName || branding?.productName || "NetzERP"

  useEffect(() => {
    if (prefsAppliedRef.current || !serverPrefs) return
    prefsAppliedRef.current = true
    if (Array.isArray(serverPrefs.sidebarFavorites)) {
      const cleanedFavorites = serverPrefs.sidebarFavorites.filter((href: string) => !NON_FAVORITE_HREFS.has(href))
      setFavorites(cleanedFavorites)
      saveFavorites(cleanedFavorites)
    }
  }, [serverPrefs])

  const isFavorite = (href: string) => !NON_FAVORITE_HREFS.has(href) && favorites.includes(href)

  // Sektions-Schluessel (muessen mit den keys in <SectionHeader> uebereinstimmen)
  const SECTION_KEYS = ["werkbank", "projekte", "tickets", "dsgvo", "rechnungen", "hr", "admin"]
  const allOpen = SECTION_KEYS.every((k) => sectionState[k] === true)
  const toggleAll = useCallback(() => {
    const next: Record<string, boolean> = {}
    for (const k of SECTION_KEYS) next[k] = !allOpen
    setSectionState(next)
    saveSectionState(next)
  }, [allOpen])

  const favoriteItems = allNavItems.filter((item) => {
    if (NON_FAVORITE_HREFS.has(item.href)) return false
    if (!favorites.includes(item.href)) return false
    return canSeeItem(item)
  })

  // Prueft ob ein Item in einer Sektion aktiv ist (fuer auto-expand)
  const hasActiveItem = (items: NavItem[]) =>
    items.some((item) => isActive(item.href))

  // Wiederverwendbarer Stern-Button fuer Favoriten-Toggle (gleicher Look wie im NavLink).
  const FavoriteStar = ({ href, label }: { href: string; label?: string }) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        toggleFavorite(href)
      }}
      aria-pressed={isFavorite(href)}
      aria-label={
        isFavorite(href)
          ? `${label ?? "Eintrag"} aus Favoriten entfernen`
          : `${label ?? "Eintrag"} zu Favoriten hinzufuegen`
      }
      title={isFavorite(href) ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufuegen"}
      className={cn(
        "mr-1 p-1 rounded transition-opacity hover:bg-muted shrink-0",
        isFavorite(href) ? "opacity-100" : "opacity-40 hover:opacity-100",
      )}
    >
      <Star
        className={cn(
          "h-3.5 w-3.5",
          isFavorite(href)
            ? "fill-yellow-400 text-yellow-400"
            : "text-muted-foreground hover:text-yellow-500 hover:fill-yellow-200",
        )}
      />
    </button>
  )

  const NavLink = ({ item, showFavoriteToggle = false }: { item: NavItem; showFavoriteToggle?: boolean }) => {
    const Icon = item.icon
    const active = isActive(item.href)

    const linkContent = (
      <div className="group flex items-center">
        <Link
          to={item.href}
          className={cn(
            "flex flex-1 items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-primary/10",
            active && "bg-primary/10 text-primary font-medium border-l-4 border-primary -ml-0.5 pl-2.5",
            collapsed && "justify-center px-2"
          )}
        >
          <Icon className={cn("h-4 w-4 shrink-0 text-muted-foreground/60", active && "text-primary")} />
          {!collapsed && <span className="truncate">{item.title}</span>}
        </Link>
        {!collapsed && showFavoriteToggle && (
          <FavoriteStar href={item.href} label={item.title} />
        )}
      </div>
    )

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              to={item.href}
              className={cn(
                "flex items-center justify-center rounded-lg px-2 py-2 text-sm transition-all hover:bg-primary/10",
                active && "bg-primary/10 text-primary font-medium border-l-4 border-primary -ml-0.5 pl-1.5",
              )}
            >
              <Icon className={cn("h-4 w-4 text-muted-foreground/60", active && "text-primary")} />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">{item.title}</TooltipContent>
        </Tooltip>
      )
    }

    return linkContent
  }

  const SectionHeader = ({
    label,
    sectionKey,
    items,
    icon: SectionIcon,
  }: {
    label: string
    sectionKey: string
    items: NavItem[]
    icon?: React.ComponentType<{ className?: string }>
  }) => {
    const hasActive = hasActiveItem(items)
    // Default: alles zugeklappt. User-Aenderungen ueberschreiben das.
    const isOpen = sectionState[sectionKey] ?? false

    return (
      <Collapsible
        open={isOpen}
        onOpenChange={() => {
          const willClose = isOpen
          toggleSection(sectionKey)
          if (willClose && hasActive) {
            navigate("/hr/my")
          }
        }}
      >
        <div className="mt-2">
          {!collapsed ? (
            <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-1.5 hover:bg-muted/50 rounded-md transition-colors">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {label}
              </span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 text-muted-foreground transition-transform",
                  isOpen && "rotate-180"
                )}
              />
            </CollapsibleTrigger>
          ) : (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <CollapsibleTrigger className={cn(
                  "flex w-full items-center justify-center rounded-lg px-2 py-2 transition-colors hover:bg-muted/50",
                  hasActive && "bg-primary/10"
                )}>
                  {SectionIcon ? (
                    <SectionIcon className={cn("h-5 w-5 text-muted-foreground", hasActive && "text-primary")} />
                  ) : (
                    <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
                  )}
                </CollapsibleTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">{label}</TooltipContent>
            </Tooltip>
          )}
          <CollapsibleContent>
            <nav className={cn("space-y-1 px-2 mt-1", collapsed && "mt-0")}>
              {items.map((item) => (
                <NavLink key={item.href} item={item} showFavoriteToggle={!collapsed} />
              ))}
            </nav>
          </CollapsibleContent>
        </div>
      </Collapsible>
    )
  }

  return (
    <aside
      className={cn(
        "flex flex-col border-r bg-card transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo — fuellt den Bereich ueber dem Menue aus */}
      <div className={cn(
        "flex h-16 items-center border-b",
        collapsed ? "justify-center px-2" : "px-3 py-2"
      )}>
        <Link to="/hr/my" className={cn("flex items-center", !collapsed && "h-full w-full justify-center")}>
          <img
            src={sidebarLogoUrl}
            alt={sidebarLogoAlt}
            className={cn("object-contain", collapsed ? "h-9 w-auto max-w-10" : "h-full w-full")}
          />
        </Link>
      </div>

      {/* Mein Arbeitsplatz — immer ganz oben, vor Schnellnavigation und Favoriten. */}
      <div className="border-b px-2 py-2">
        <NavLink
          item={{ title: "Mein Arbeitsplatz", href: "/hr/my", icon: Users }}
          showFavoriteToggle={false}
        />
      </div>

      {/* Cmd+K Hint + Toggle-Button (nur wenn nicht collapsed) */}
      {!collapsed && (
        <div className="border-b px-3 py-2 space-y-2">
          <button
            type="button"
            onClick={onOpenPalette}
            className="w-full flex items-center gap-2 rounded-md border bg-secondary/50 px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-secondary transition-colors"
          >
            <Command className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 truncate text-left">Schnellsuche</span>
            <kbd className="text-[10px] font-mono bg-background px-1.5 py-0.5 rounded border">
              {isMac ? "⌘" : "Ctrl"}+K
            </kbd>
          </button>
          <button
            type="button"
            onClick={toggleAll}
            className="w-full flex items-center gap-2 rounded-md px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-muted/50 transition-colors"
            title={allOpen ? "Alle Sektionen zuklappen" : "Alle Sektionen aufklappen"}
          >
            {allOpen ? <ChevronsDownUp className="h-3 w-3" /> : <ChevronsUpDown className="h-3 w-3" />}
            <span className="flex-1 text-left">{allOpen ? "Alle zuklappen" : "Alle aufklappen"}</span>
          </button>
        </div>
      )}

      {/* Favoriten — immer sichtbar, nicht scrollbar */}
      {favoriteItems.length > 0 ? (
        <div className="border-b px-0 pt-3 pb-2 bg-yellow-50/30 dark:bg-yellow-950/10">
          {!collapsed && (
            <div className="px-3 mb-1">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                Favoriten
              </span>
            </div>
          )}
          <nav className="space-y-1 px-2">
            {favoriteItems.map((item) => (
              <NavLink key={`fav-${item.href}`} item={item} showFavoriteToggle={!collapsed} />
            ))}
          </nav>
        </div>
      ) : !collapsed && (
        <div className="border-b px-3 py-2 text-[11px] text-muted-foreground bg-muted/20">
          <span className="flex items-center gap-1.5">
            <Star className="h-3 w-3 shrink-0" />
            Tipp: Mit <Star className="h-3 w-3 inline" /> Eintraege als Favoriten anpinnen
          </span>
        </div>
      )}

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4 pr-2.5">
        {/* Werkbank Section */}
        {filteredArbeitsplatzItems.length > 0 && (
          <SectionHeader label="Werkbank" sectionKey="werkbank" items={filteredArbeitsplatzItems} icon={LayoutDashboard} />
        )}

        {/* Projekte Section */}
        {filteredProjektItems.length > 0 && (
          <SectionHeader label="Projekte" sectionKey="projekte" items={filteredProjektItems} icon={FolderTree} />
        )}

        {/* Tickets Section */}
        {filteredTicketItems.length > 0 && (
          <SectionHeader label={`Tickets${ticketUnread > 0 ? ` (${ticketUnread})` : ""}`} sectionKey="tickets" items={filteredTicketItems} icon={Kanban} />
        )}

        {/* Collapsible Sections */}
        {filteredDsgvoItems.length > 0 && (
          <SectionHeader label="DSGVO" sectionKey="dsgvo" items={filteredDsgvoItems} icon={Shield} />
        )}
        {filteredRechnungenItems.length > 0 && (
          <SectionHeader label="Rechnungen" sectionKey="rechnungen" items={filteredRechnungenItems} icon={Receipt} />
        )}
        {filteredHrNavItems.length > 0 && (
          <SectionHeader label="Personal" sectionKey="hr" items={filteredHrNavItems} icon={UsersRound} />
        )}
        {filteredAdminItems.length > 0 && (
          <SectionHeader label="Administration" sectionKey="admin" items={filteredAdminItems} icon={Settings} />
        )}
      </ScrollArea>

      {/* Footer */}
      <div className={cn(
        "border-t p-4 text-xs text-muted-foreground",
        collapsed && "p-2 text-center"
      )}>
        {collapsed ? (
          <span>1.0</span>
        ) : (
          <span>ERP 1.0.0</span>
        )}
      </div>
    </aside>
  )
}
